import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/** Колонка шапки: подпись и, если нужно, свой класс ячейки — числовой колонке нужен центр. */
export interface ImportListColumn {
  readonly label: string;
  readonly class?: string;
}

/** Раскладка шапки: треки колонок приходят стилем — они общие у шапки со строками вызывающего. */
const HEAD_CLASS =
  'grid gap-3 px-4 sticky top-0 z-1 border-b border-line bg-panel2 py-2 ' +
  'text-2xs font-semibold uppercase tracking-[var(--ls-caps)] text-c3';

/**
 * Класс проецируемой строки. Вызывающий вешает его на свою строку вместе с тем же
 * `grid-template-columns`, что отдал списку, — иначе строка разъедется с шапкой.
 */
export const IMPORT_LIST_ROW_CLASS =
  'group grid gap-3 px-4 text-sm text-c2 transition-colors duration-[var(--t-mid)] hover:bg-acc-softest ' +
  'min-h-11 items-center border-b border-line last:border-b-0';

const TAIL_CLASS =
  'block w-full px-4 py-2.5 text-left text-sm font-semibold text-acc hover:bg-acc-softer ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acc-line';

/**
 * Двухколоночный список витрин импорта: рамка со скроллом, липкая шапка по `columns` и хвост
 * «…и ещё N — показать» под спроецированными строками. Строки рисует вызывающий — их содержимое
 * у каждой витрины своё, общая здесь только оправа.
 */
@Component({
  selector: 'app-import-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block max-h-[var(--max-h-dialog-scroll)] overflow-y-auto rounded-[var(--r)] border border-line',
  },
  template: `
    <div [class]="headClass" [style.grid-template-columns]="tracks()">
      @for (column of columns(); track column.label) {
        <span [class]="column.class ?? ''">{{ column.label }}</span>
      }
    </div>
    <ng-content />
    @if (tail(); as label) {
      <button type="button" [class]="tailClass" [attr.data-testid]="tailTestid()" (click)="tailOpened.emit()">
        {{ label }}
      </button>
    }
  `,
})
export class ImportListComponent {
  readonly columns = input.required<readonly ImportListColumn[]>();
  /** `grid-template-columns` списка: одна раскладка на шапку и на строки вызывающего. */
  readonly tracks = input.required<'minmax(0,1fr)' | 'minmax(0,1fr) 90px' | 'var(--layout-import-preview)'>();
  /** Подпись хвоста; пусто — список показан целиком и кнопки раскрытия нет. */
  readonly tail = input('');
  readonly tailTestid = input<string | null>(null);
  readonly tailOpened = output<void>();

  protected readonly headClass = HEAD_CLASS;
  protected readonly tailClass = TAIL_CLASS;
}
